import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getAirbandTranscripts, getAirbandStats, saveAirbandLabel } from '../utils/httpApi';
import { getAccessToken } from '../utils/auth';
import { isApiSuccess } from '../utils/api';
import './Airband.css';

/** Local yyyy-MM-dd for the date picker default. */
function todayIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Backend timestamps are UTC LocalDateTime strings; render local HH:MM:SS. */
function fmtTime(ts) {
  if (!ts) return '—';
  const iso = ts.includes('T') ? ts : ts.replace(' ', 'T');
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return ts;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Human label for a match_strength value from the post-retranscription callsign check. */
function matchLabel(strength) {
  if (strength === 'FULL') return 'Full';
  if (strength === 'NUMBER_ONLY') return 'Number-only';
  if (strength === 'PARTIAL') return 'Partial';
  return 'No';
}

function confClass(conf) {
  if (conf == null) return '';
  if (conf >= 0.7) return 'conf-good';
  if (conf >= 0.45) return 'conf-mid';
  return 'conf-low';
}

/**
 * Airband voice transcripts view. Lets a user (especially a headless-Pi operator) see the transcribed
 * ATC voice their receiver produced, plus per-channel stats. Mirrors the Messages (ACARS) view's
 * receiver/date controls and table styling; all colours come from the theme variables.
 */
function Airband({ receivers = [], viewerReceivers = [], selectedReceiverUuid, onReceiverChange }) {
  const allReceivers = useMemo(() => [...receivers, ...viewerReceivers], [receivers, viewerReceivers]);

  const [localReceiverUuid, setLocalReceiverUuid] = useState(selectedReceiverUuid || '');
  const [date, setDate] = useState(todayIso());
  const [transcripts, setTranscripts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  // One shared Audio element: playing a second clip stops the first, so the page never overlaps two
  // transmissions. The presigned URL on each transcript is short-lived, so we never cache it.
  const audioRef = useRef(null);

  // Per-row labelling: which row's editor is open, the editable text, the playback speed (for hard-to-hear
  // clips), a save status message, and an in-flight flag.
  const [editingId, setEditingId] = useState(null);
  const [labelText, setLabelText] = useState('');
  const [playRate, setPlayRate] = useState(1);
  const [saveMsg, setSaveMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const togglePlay = useCallback((t) => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', () => setPlayingId(null));
      audioRef.current.addEventListener('error', () => setPlayingId(null));
    }
    const audio = audioRef.current;
    if (playingId === t.id) {
      audio.pause();
      setPlayingId(null);
      return;
    }
    if (!t.audioUrl) return;
    audio.src = t.audioUrl;
    audio.playbackRate = playRate;
    audio.play().then(() => setPlayingId(t.id)).catch(() => setPlayingId(null));
  }, [playingId, playRate]);

  // Keep an already-playing clip in sync when the speed control changes.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playRate;
  }, [playRate]);

  // Stop any playback when the receiver or date changes, so a click doesn't resume an unrelated clip.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlayingId(null);
  }, [localReceiverUuid, date]);

  useEffect(() => {
    if (selectedReceiverUuid && selectedReceiverUuid !== 'ALL_RECEIVERS') {
      setLocalReceiverUuid(selectedReceiverUuid);
    } else if (!localReceiverUuid && allReceivers.length > 0) {
      setLocalReceiverUuid(allReceivers[0].receiverUuid);
    }
  }, [selectedReceiverUuid, allReceivers, localReceiverUuid]);

  const handleReceiverChange = useCallback((e) => {
    const uuid = e.target.value;
    setLocalReceiverUuid(uuid);
    const r = allReceivers.find(x => x.receiverUuid === uuid);
    if (r && onReceiverChange) onReceiverChange(r.receiverName);
  }, [allReceivers, onReceiverChange]);

  const load = useCallback(async () => {
    if (!localReceiverUuid) return;
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const [tRes, sRes] = await Promise.all([
        getAirbandTranscripts(localReceiverUuid, date, token, 500),
        getAirbandStats(localReceiverUuid, date, token),
      ]);
      setTranscripts(tRes?.transcripts || []);
      setStats(sRes || null);
    } catch (e) {
      setError(e?.message || 'Failed to load airband data');
      setTranscripts([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [localReceiverUuid, date]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh today's view every 15s so a live headless Pi's transmissions appear.
  useEffect(() => {
    if (date !== todayIso()) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [date, load]);

  // Open the label editor for a row, seeding the box with the best transcript we already have (refined,
  // else live) so the operator corrects rather than types from scratch.
  const openEditor = useCallback((t) => {
    setEditingId(t.id);
    setLabelText(t.humanLabel || t.retranscribedText || t.transcriptText || '');
    setSaveMsg(null);
  }, []);

  const closeEditor = useCallback(() => {
    setEditingId(null);
    setSaveMsg(null);
  }, []);

  // Save a label ('labeled' with the corrected text) or mark 'ignored' (unintelligible, no text). On success
  // update the row in place so the badge shows without a reload.
  const saveLabel = useCallback(async (t, status) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const token = getAccessToken();
      const text = status === 'ignored' ? '' : labelText.trim();
      const res = await saveAirbandLabel(localReceiverUuid, t.id, text, status, token);
      if (isApiSuccess(res)) {
        setTranscripts(prev => prev.map(x =>
          x.id === t.id ? { ...x, humanLabel: text || null, labelStatus: status } : x));
        setEditingId(null);
      } else {
        setSaveMsg({ type: 'err', text: 'Save failed — please try again' });
      }
    } catch (e) {
      setSaveMsg({ type: 'err', text: e?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  }, [labelText, localReceiverUuid]);

  return (
    <div className="airband-page">
      <div className="airband-controls">
        <div className="receiver-display">
          <span className="receiver-label">Receiver:</span>
          <select className="receiver-select" value={localReceiverUuid || ''} onChange={handleReceiverChange}>
            {allReceivers.length === 0 && <option value="">No receivers</option>}
            {allReceivers.map(r => (
              <option key={r.receiverUuid} value={r.receiverUuid}>{r.receiverName || r.displayName}</option>
            ))}
          </select>
        </div>
        <div className="airband-date">
          <span className="receiver-label">Date:</span>
          <input type="date" className="airband-date-input" value={date} max={todayIso()}
                 onChange={(e) => setDate(e.target.value)} />
        </div>
        <button className="airband-refresh" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {stats && (
        <div className="airband-stats">
          <div className="airband-stat">
            <span className="airband-stat-value">{stats.totalMessages ?? 0}</span>
            <span className="airband-stat-label">transmissions</span>
          </div>
          <div className="airband-stat">
            <span className="airband-stat-value">{stats.callsignMatched ?? 0}</span>
            <span className="airband-stat-label">ADS-B matched</span>
          </div>
          <div className="airband-stat">
            <span className="airband-stat-value">{(stats.channels || []).length}</span>
            <span className="airband-stat-label">channels</span>
          </div>
          {(stats.channels || []).length > 0 && (
            <div className="airband-channel-chips">
              {stats.channels.map(c => (
                <span key={c.channelMhz} className="airband-channel-chip"
                      title={`${c.messageCount} msgs · avg conf ${(c.avgConfidence || 0).toFixed(2)}`
                             + (c.avgRssiDb ? ` · avg ${c.avgRssiDb.toFixed(0)} dB` : '')}>
                  {c.channelMhz.toFixed(3)} <b>{c.messageCount}</b>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <div className="airband-error">{error}</div>}

      <div className="airband-table-wrap">
        <table className="airband-table">
          <thead>
            <tr>
              <th>Time</th>
              <th className="num">Channel</th>
              <th>Callsign</th>
              <th title="Best callsign match of the refined transcript against aircraft the operator's receivers tracked around the transmission (full or partial)">Matched</th>
              <th className="num">Conf</th>
              <th className="num" title="Peak carrier-to-noise ratio during the transmission">SNR</th>
              <th>Transcript</th>
              <th title="High-quality server-side re-transcription (large-v3 ATC model)">Refined (HQ)</th>
              <th title="Correct the transcript to build the fine-tune training set">Label</th>
            </tr>
          </thead>
          <tbody>
            {transcripts.length === 0 && !loading && (
              <tr className="airband-empty">
                <td colSpan="9">
                  No transcripts for this receiver on {date}. Airband voice must be enabled on the
                  receiver, with upload turned on, for transmissions to appear here.
                </td>
              </tr>
            )}
            {transcripts.map(t => (
              <React.Fragment key={t.id}>
              <tr className={t.truncated ? 'airband-truncated' : ''}>
                <td>{fmtTime(t.messageTimestamp)}</td>
                <td className="num">{t.channelMhz != null ? t.channelMhz.toFixed(3) : '—'}</td>
                <td>{t.callsign
                  ? <span className="airband-callsign" title="Corroborated by ADS-B">✈ {t.callsign}</span>
                  : <span className="airband-nomatch">—</span>}</td>
                <td>{t.matchedCallsign
                  ? <span className="airband-callsign"
                      title={`${matchLabel(t.matchStrength)} match, score ${t.matchScore != null ? Math.round(t.matchScore * 100) : '—'}%`}>
                      {t.matchedCallsign}{' '}
                      <span className={`status-badge ${t.matchStrength === 'FULL' ? 'new' : 'regular'}`}>
                        {t.matchStrength === 'FULL' ? 'full' : 'partial'}</span>
                    </span>
                  : <span className="airband-nomatch"
                      title={t.matchedAt ? 'Checked: no aircraft in view matched' : 'Not yet checked (runs after the HQ re-transcription)'}>—</span>}</td>
                <td className={`num ${confClass(t.confidence)}`}>
                  {t.confidence != null ? Math.round(t.confidence * 100) + '%' : '—'}</td>
                <td className="num">{t.rssiDb != null ? t.rssiDb.toFixed(0) + ' dB' : '—'}</td>
                <td className="airband-text">
                  {t.audioUrl && (
                    <button
                      type="button"
                      className={`airband-play${playingId === t.id ? ' playing' : ''}`}
                      title="Play the recorded transmission"
                      aria-label="Play recording"
                      onClick={() => togglePlay(t)}
                    >{playingId === t.id ? '❚❚' : '▶'}</button>
                  )}
                  {t.transcriptText}
                  {t.truncated && <span className="airband-cut"> (cut)</span>}</td>
                <td className="airband-text airband-hq-cell">
                  {t.retranscribedText
                    ? <span className="airband-hq-text">{t.retranscribedText}</span>
                    : <span className="airband-nomatch" title="Not yet re-transcribed (queued for the GPU pass)">—</span>}</td>
                <td className="airband-label-cell">
                  {t.audioUrl
                    ? <button type="button"
                        className={`airband-label-btn${t.labelStatus ? ' ' + t.labelStatus : ''}`}
                        title="Correct this transcript for the training set"
                        onClick={() => (editingId === t.id ? closeEditor() : openEditor(t))}
                      >{t.labelStatus === 'labeled' ? '✓ labeled'
                        : t.labelStatus === 'ignored' ? '⃠ ignored'
                        : '✎ label'}</button>
                    : <span className="airband-nomatch">—</span>}
                </td>
              </tr>
              {editingId === t.id && (
                <tr className="airband-label-editor-row">
                  <td colSpan="9">
                    <div className="airband-label-editor">
                      <div className="airband-label-controls">
                        {t.audioUrl && (
                          <button type="button" className="airband-label-play" onClick={() => togglePlay(t)}>
                            {playingId === t.id ? '❚❚ Pause' : '▶ Play'}
                          </button>
                        )}
                        <label className="airband-label-speed">Speed
                          <select value={playRate} onChange={e => setPlayRate(parseFloat(e.target.value))}>
                            <option value={0.5}>0.5×</option>
                            <option value={0.75}>0.75×</option>
                            <option value={1}>1×</option>
                          </select>
                        </label>
                      </div>
                      <textarea className="airband-label-text" spellCheck={false}
                        value={labelText} onChange={e => setLabelText(e.target.value)}
                        placeholder="What was actually said (leave blank and Ignore if unintelligible)" />
                      <div className="airband-label-actions">
                        <button type="button" className="airband-label-save" disabled={saving}
                          onClick={() => saveLabel(t, 'labeled')}>{saving ? 'Saving…' : 'Save'}</button>
                        <button type="button" className="airband-label-ignore" disabled={saving}
                          onClick={() => saveLabel(t, 'ignored')}>Ignore</button>
                        <button type="button" className="airband-label-cancel" disabled={saving}
                          onClick={closeEditor}>Cancel</button>
                        {saveMsg && <span className={`airband-label-msg ${saveMsg.type}`}>{saveMsg.text}</span>}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Airband;
